import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  BookOpen, BotMessageSquare, Sparkles, Briefcase, Share2, Zap,
  ChevronRight, ChevronLeft, X, FolderOpen, Scale, Search, Highlighter
} from 'lucide-react';

const TOUR_KEY = 'lavidalex_tour_completed';

const STEPS = [
  {
    navTarget: 'nav-library',
    route: '/library',
    icon: BookOpen,
    color: 'from-blue-500 to-indigo-600',
    title: 'Law Library',
    intro: 'Browse Philippine laws organized by codal categories such as Civil, Commercial, Criminal, Labor, Ethics, Political, Remedial, Taxation, and more.',
    features: [
      { label: 'Browse by Category', text: 'Tap any category card to explore its laws and drill into articles and provisions.' },
      { label: 'Favorites', text: 'Star frequently used laws so they appear at the top for quick access.' },
      { label: 'Recent Access', text: 'Quickly return to laws you recently viewed.' },
    ],
    tip: 'Star your frequently used laws to save them to Favorites for quick access.',
  },
  {
    navTarget: 'nav-zap-tools',
    route: '/zap-tools',
    icon: Zap,
    color: 'from-yellow-500 to-amber-600',
    title: 'Zap Tools',
    intro: 'Your quick-access research toolkit with three powerful tools, each designed for a specific research need.',
    features: [
      { label: 'Provision Finder', text: 'Search specific provisions across all laws by keyword, article number, or section title. Results link directly to the full text.' },
      { label: 'Jurisprudence Finder', text: 'AI-powered search across 36,000+ Supreme Court decisions. Browse cases, compare them side-by-side, and access the 5-step case analysis.', highlight: 'Its Case Digest is like no other; the format is tailored for lawyers\' rapid doctrinal grasp.' },
      { label: 'QUAMTO Masterclass', text: 'Review past bar exam questions with suggested answers, organized by subject.' },
    ],
    tip: 'Use the Share icon in the top header to share your referral code with colleagues!',
  },
  {
    navTarget: 'nav-consult-ai',
    route: '/search',
    icon: BotMessageSquare,
    color: 'from-purple-500 to-violet-600',
    title: 'Consult AI (Lois)',
    intro: 'Ask Lois any legal question in plain language. It searches across statutes and jurisprudence, then answers with full citations.',
    features: [
      { label: 'ALAC Format', text: 'Every answer follows Answer, Legal Basis, Application, and Conclusion.' },
      { label: 'Deeper Search', text: 'After its initial answer, Lois can search deeper for additional cases you might have missed.' },
      { label: 'Consultation History', text: 'All your past consultations are saved for easy reference.' },
    ],
    tip: 'Try asking: "What are the elements of estafa under the RPC?"',
  },
  {
    navTarget: 'nav-workspace',
    route: '/workspace',
    icon: Briefcase,
    color: 'from-emerald-500 to-teal-600',
    title: 'Workspace',
    intro: 'Your personal legal workspace to organize everything you save across the platform.',
    features: [
      { label: 'Provisions', text: 'Saved bookmarks, notes, and highlights from statutes, organized into collections.' },
      { label: 'Jurisprudence', text: 'Text annotations and Lois Case Digests from Supreme Court decisions, with View Source to jump back to the full 5-step case analysis.' },
    ],
    tip: 'Use Collections to group related provisions and cases into organized folders.',
  },
  {
    navTarget: 'nav-synthesis',
    route: '/synthesis',
    icon: Sparkles,
    color: 'from-amber-500 to-orange-600',
    title: 'Synthesis',
    intro: 'AI-assisted legal research and litigation preparation with two specialized tools.',
    features: [
      { label: 'Research Briefer', text: 'Generate structured, citation-backed legal documents in 4 steps: Draft, Review & Verify, Revise, and Finalize. Each cited reference can be verified against the internal library.' },
      { label: 'Paraphraser', text: 'Convert legal text into formal or simplified language while preserving legal accuracy.' },
    ],
    tip: 'Click any [Ref] citation to open the verification panel and check its source.',
  },
];

export function resetTour() {
  localStorage.removeItem(TOUR_KEY);
}

export function isTourCompleted() {
  return localStorage.getItem(TOUR_KEY) === 'true';
}

export default function TutorialWalkthrough({ forceOpen = false, onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [active, setActive] = useState(false);
  const [step, setStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const cardRef = useRef(null);

  // Auto-show for first time users
  useEffect(() => {
    if (forceOpen) {
      setStep(0);
      setActive(true);
      return;
    }
    const completed = localStorage.getItem(TOUR_KEY);
    if (!completed) {
      const timer = setTimeout(() => {
        setStep(0);
        setActive(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [forceOpen]);

  const measureTarget = useCallback(() => {
    if (!active) return;
    const current = STEPS[step];
    const el = document.querySelector(`[data-testid="${current.navTarget}"]`);
    if (el) {
      const rect = el.getBoundingClientRect();
      setTargetRect(rect);
    }
  }, [active, step]);

  useEffect(() => {
    measureTarget();
    window.addEventListener('resize', measureTarget);
    window.addEventListener('scroll', measureTarget);
    return () => {
      window.removeEventListener('resize', measureTarget);
      window.removeEventListener('scroll', measureTarget);
    };
  }, [measureTarget]);

  // Navigate to the step's route
  useEffect(() => {
    if (!active) return;
    const current = STEPS[step];
    if (location.pathname !== current.route) {
      navigate(current.route);
    }
    // Re-measure after navigation
    const t = setTimeout(measureTarget, 300);
    return () => clearTimeout(t);
  }, [active, step]);

  const complete = () => {
    localStorage.setItem(TOUR_KEY, 'true');
    setActive(false);
    setStep(0);
    navigate('/library');
    if (onClose) onClose();
  };

  const next = () => {
    if (step < STEPS.length - 1) setStep(s => s + 1);
    else complete();
  };

  const prev = () => {
    if (step > 0) setStep(s => s - 1);
  };

  if (!active) return null;

  const current = STEPS[step];
  const Icon = current.icon;
  const progress = ((step + 1) / STEPS.length) * 100;

  // Spotlight cutout dimensions
  const pad = 6;
  const spotX = targetRect ? targetRect.left - pad : 0;
  const spotY = targetRect ? targetRect.top - pad : 0;
  const spotW = targetRect ? targetRect.width + pad * 2 : 0;
  const spotH = targetRect ? targetRect.height + pad * 2 : 0;

  return (
    <div className="fixed inset-0 z-[9999]" data-testid="tutorial-overlay">
      {/* Overlay with spotlight cutout */}
      <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: 'none' }}>
        <defs>
          <mask id="spotlight-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {targetRect && (
              <rect
                x={spotX} y={spotY}
                width={spotW} height={spotH}
                rx="12" fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          x="0" y="0" width="100%" height="100%"
          fill="rgba(0,0,0,0.7)"
          mask="url(#spotlight-mask)"
        />
      </svg>

      {/* Spotlight ring pulse */}
      {targetRect && (
        <div
          className="absolute border-2 border-purple-400 rounded-xl animate-pulse pointer-events-none"
          style={{
            left: spotX, top: spotY,
            width: spotW, height: spotH,
            boxShadow: '0 0 0 4px rgba(168,85,247,0.3)',
          }}
        />
      )}

      {/* Clickable overlay to dismiss */}
      <div className="absolute inset-0" onClick={complete} />

      {/* Tutorial card — centered on screen */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none px-4">
        <div
          ref={cardRef}
          className="w-full max-w-lg bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-slate-700 overflow-hidden pointer-events-auto"
          onClick={e => e.stopPropagation()}
          data-testid="tutorial-card"
        >
        {/* Progress bar */}
        <div className="h-1 bg-gray-200 dark:bg-slate-700">
          <div
            className="h-full bg-gradient-to-r from-purple-500 to-emerald-500 transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className="p-5 sm:p-6">
          {/* Header */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-xl bg-gradient-to-br ${current.color} text-white shadow-lg`}>
                <Icon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm font-semibold text-purple-600 dark:text-purple-400 uppercase tracking-wider">
                  Step {step + 1} of {STEPS.length}
                </p>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">{current.title}</h3>
              </div>
            </div>
            <button
              onClick={complete}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1"
              data-testid="tutorial-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Description */}
          <p className="text-base text-gray-600 dark:text-gray-300 leading-relaxed mb-3">
            {current.intro}
          </p>

          {/* Features */}
          {current.features && current.features.length > 0 && (
            <div className="space-y-2 mb-3">
              {current.features.map((f, i) => (
                <div key={i} className="flex items-start gap-2.5 bg-gray-50 dark:bg-slate-700/50 rounded-lg px-3 py-2">
                  <span className="text-purple-600 dark:text-purple-400 font-bold text-base mt-0.5 shrink-0">{i + 1}.</span>
                  <div>
                    <span className="font-semibold text-gray-800 dark:text-gray-200 text-base">{f.label}</span>
                    <span className="text-gray-600 dark:text-gray-400 text-sm ml-1">{f.text}</span>
                    {f.highlight && <span className="font-bold text-gray-800 dark:text-gray-200 text-sm ml-1">{f.highlight}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Tip */}
          <div className="bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-700 rounded-lg px-3 py-2 mb-4">
            <p className="text-sm text-purple-700 dark:text-purple-300">
              <span className="font-semibold">Tip:</span> {current.tip}
            </p>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <button
              onClick={complete}
              className="text-sm text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 underline"
              data-testid="tutorial-skip"
            >
              Skip tutorial
            </button>
            <div className="flex items-center gap-2">
              {step > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={prev}
                  className="text-sm"
                  data-testid="tutorial-prev"
                >
                  <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Back
                </Button>
              )}
              <Button
                size="sm"
                onClick={next}
                className="bg-purple-600 hover:bg-purple-700 text-white text-sm"
                data-testid="tutorial-next"
              >
                {step < STEPS.length - 1 ? (
                  <>Next <ChevronRight className="w-3.5 h-3.5 ml-1" /></>
                ) : (
                  'Get Started!'
                )}
              </Button>
            </div>
          </div>

          {/* Step dots */}
          <div className="flex justify-center gap-1.5 mt-3">
            {STEPS.map((_, i) => (
              <button
                key={i}
                onClick={() => setStep(i)}
                className={`w-2 h-2 rounded-full transition-all ${
                  i === step ? 'bg-purple-600 w-5' : 'bg-gray-300 dark:bg-slate-600'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
